import React, { useState, useEffect } from 'react';
import { schedulerJobAPI, planAPI } from '../../services/api';
import { useBranchStore } from '../../store';
import { toast } from 'react-toastify';
import './SchedulerJobs.css';
import DateInput from '../../components/DateInput/DateInput';

const ACTIONS = [
    { value: 'upgrade_plan', label: 'Upgrade Plan' },
    { value: 'downgrade_plan', label: 'Downgrade Plan' },
    { value: 'enable_account', label: 'Enable Account' },
    { value: 'disable_account', label: 'Disable Account' },
    { value: 'extend_validity', label: 'Extend Validity' },
];

const DEFAULT_FORM = { userId: '', action: 'upgrade_plan', targetPlanId: '', extendMinutes: '', runAt: '', recurring: 'none', reason: '' };

const SchedulerJobs = () => {
    const { selectedBranch } = useBranchStore();
    const [jobs, setJobs] = useState([]);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState(DEFAULT_FORM);

    useEffect(() => { fetchAll(); }, [selectedBranch]);

    const fetchAll = async () => {
        try {
            setLoading(true);
            const branchId = selectedBranch ? selectedBranch._id : undefined;
            const [jobsRes, plansRes] = await Promise.all([
                schedulerJobAPI.getAll(branchId ? { branchId } : undefined),
                planAPI.getAll(branchId),
            ]);
            setJobs(jobsRes.data.data || []);
            setPlans(plansRes.data.data.plans || []);
        } catch (error) {
            toast.error('Failed to fetch scheduled jobs');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.userId.trim()) return toast.warning('User ID is required');
        if (!formData.runAt) return toast.warning('Run-at time is required');
        try {
            await schedulerJobAPI.create(formData);
            toast.success('Job scheduled');
            setShowModal(false);
            setFormData(DEFAULT_FORM);
            fetchAll();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to schedule job');
        }
    };

    const handleCancel = async (job) => {
        try {
            await schedulerJobAPI.cancel(job._id);
            toast.success('Job cancelled');
            fetchAll();
        } catch (error) {
            toast.error('Failed to cancel job');
        }
    };

    const handleDelete = async (job) => {
        if (!window.confirm('Delete this scheduled job?')) return;
        try {
            await schedulerJobAPI.delete(job._id);
            toast.success('Job deleted');
            fetchAll();
        } catch (error) {
            toast.error('Failed to delete job');
        }
    };

    const statusColor = (s) => ({ pending: '#3b82f6', completed: '#16a34a', failed: '#dc2626', cancelled: '#6b7280' }[s] || '#6b7280');

    return (
        <div className="page-container-modern">
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern"><i className="fas fa-calendar-alt"></i> Plan Scheduler</h1>
                        <p className="page-subtitle-modern">Schedule future plan upgrades, account enable/disable, or validity extensions. Runs automatically in the background every minute — plus auto-expiry and room auto-checkout sweeps every 5 minutes.</p>
                    </div>
                    <div className="header-actions">
                        <button className="btn-primary-modern" onClick={() => setShowModal(true)}><i className="fas fa-plus-circle"></i> Schedule Job</button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="loading-modern"><div className="spinner-modern"></div><p>Loading jobs...</p></div>
            ) : jobs.length === 0 ? (
                <div className="empty-state-modern">
                    <div className="empty-icon"><i className="fas fa-calendar-alt"></i></div>
                    <h3>No Scheduled Jobs</h3>
                    <p>Auto-expiry, auto-renew, and room auto-checkout already run automatically — this is only for one-off future actions, e.g. "upgrade this guest to VIP at 8 AM".</p>
                </div>
            ) : (
                <div className="table-wrapper-modern">
                    <table className="table-modern">
                        <thead>
                            <tr><th>Action</th><th>Run At</th><th>Recurring</th><th>Status</th><th>Last Run</th><th></th></tr>
                        </thead>
                        <tbody>
                            {jobs.map(job => (
                                <tr key={job._id}>
                                    <td>{ACTIONS.find(a => a.value === job.action)?.label || job.action} {job.targetPlanId?.name ? `→ ${job.targetPlanId.name}` : ''}</td>
                                    <td>{new Date(job.runAt).toLocaleString('en-IN')}</td>
                                    <td style={{ textTransform: 'capitalize' }}>{job.recurring}</td>
                                    <td><span className="status-badge" style={{ background: `${statusColor(job.status)}15`, color: statusColor(job.status) }}>{job.status}</span></td>
                                    <td>{job.lastRunAt ? new Date(job.lastRunAt).toLocaleString('en-IN') : '—'}</td>
                                    <td>
                                        {job.status === 'pending' && (
                                            <button className="action-btn-icon" onClick={() => handleCancel(job)} title="Cancel"><i className="fas fa-ban"></i></button>
                                        )}
                                        <button className="action-btn-icon delete" onClick={() => handleDelete(job)} title="Delete"><i className="fas fa-trash"></i></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showModal && (
                <div className="modal-overlay-modern" onClick={() => setShowModal(false)}>
                    <div className="modal-modern" onClick={e => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <h2>Schedule Job</h2>
                            <button className="modal-close" onClick={() => setShowModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body-modern">
                                <div className="form-grid">
                                    <div className="form-group-modern full-width">
                                        <label>User ID <span style={{ color: 'var(--danger-color)' }}>*</span></label>
                                        <input type="text" value={formData.userId} onChange={e => setFormData({ ...formData, userId: e.target.value })} placeholder="MongoDB User _id" required />
                                        <p className="input-hint">Find this from the Connected Users or Users list.</p>
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Action</label>
                                        <select value={formData.action} onChange={e => setFormData({ ...formData, action: e.target.value })}>
                                            {ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                                        </select>
                                    </div>
                                    {(formData.action === 'upgrade_plan' || formData.action === 'downgrade_plan') && (
                                        <div className="form-group-modern">
                                            <label>Target Plan</label>
                                            <select value={formData.targetPlanId} onChange={e => setFormData({ ...formData, targetPlanId: e.target.value })}>
                                                <option value="">Select Plan</option>
                                                {plans.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                                            </select>
                                        </div>
                                    )}
                                    {formData.action === 'extend_validity' && (
                                        <div className="form-group-modern">
                                            <label>Extend by (minutes)</label>
                                            <input type="number" value={formData.extendMinutes} onChange={e => setFormData({ ...formData, extendMinutes: e.target.value })} />
                                        </div>
                                    )}
                                    <div className="form-group-modern">
                                        <label>Run At <span style={{ color: 'var(--danger-color)' }}>*</span></label>
                                        <DateInput type="datetime-local" value={formData.runAt} onChange={e => setFormData({ ...formData, runAt: e.target.value })} required />
                                    </div>
                                    <div className="form-group-modern">
                                        <label>Recurring</label>
                                        <select value={formData.recurring} onChange={e => setFormData({ ...formData, recurring: e.target.value })}>
                                            <option value="none">One-time</option>
                                            <option value="daily">Daily</option>
                                            <option value="weekly">Weekly</option>
                                        </select>
                                    </div>
                                    <div className="form-group-modern full-width">
                                        <label>Reason</label>
                                        <input type="text" value={formData.reason} onChange={e => setFormData({ ...formData, reason: e.target.value })} placeholder="Optional, for audit trail" />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer-modern">
                                <button type="button" className="btn-secondary-modern" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="btn-primary-modern"><i className="fas fa-check"></i> Schedule</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SchedulerJobs;
